import { getStore } from 'core_main/store';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { createSharedConversationChannelString } from 'core_main/pubnub/helpers/channelName';
import { handleError } from 'globals/errors';
import {
  CUSTOMER_TYPING_ENDED,
  CUSTOMER_TYPING,
} from 'core_main/pubnub/constants';
import { SendTypingActivityParameters } from './types';
import { validateSendTypingActivityParams } from './helpers';

export const sendTypingStatusSignal = (
  sessionId: SendTypingActivityParameters['conversationId'],
  typing: SendTypingActivityParameters['typing'],
) => {
  return importAndInitializePubNub().then((module) => {
    const pubnub = module;

    const store = getStore();

    const { sessions } = store;

    const session = sessions[sessionId];

    if (!session) return undefined;

    // Presence check only. Channel name below is rebuilt from store identity
    // because stored names can be stale after a merge.
    const channels = session.attributes?.pubnub?.data?.channels;
    const hasSharedConversationChannel = (channels || []).some(
      (channel) => channel.type === 'shared-conversation',
    );

    if (!hasSharedConversationChannel) return undefined;

    // After customer merge, session channel names can briefly retain the
    // pre-merge customer until (or unless) the store is rewritten. Build the
    // signal target from the current store identity — same approach as
    // subscribeToTypingIndicatorChannel — so PAM grants match.
    const { orgId, customerId } = store;
    if (!orgId || !customerId) return undefined;

    const sharedConversationChannelName = createSharedConversationChannelString(
      orgId,
      customerId,
      sessionId,
    );

    const signalStatus = typing ? CUSTOMER_TYPING : CUSTOMER_TYPING_ENDED;

    if (store.isPubNubConnected) {
      const signalResult = pubnub.getMetaPubNub()?.instance?.signal({
        channel: sharedConversationChannelName,
        message: signalStatus,
      });
      // PubNub v7+ signal returns a Promise; an uncaught 403 rejection surfaces
      // as a webpack-dev overlay and can interrupt post-merge recovery UX.
      if (
        signalResult &&
        typeof (signalResult as Promise<unknown>).catch === 'function'
      ) {
        return (signalResult as Promise<unknown>).catch((error) => {
          handleError(error, {
            message: 'Failed to publish typing status signal',
          });
        });
      }
    }

    return undefined;
  });
};

export const sendTypingActivity = (
  parameters: SendTypingActivityParameters,
) => {
  const sessionId = parameters?.conversationId;
  const typing = parameters?.typing;

  try {
    const validateSendTypingStatusParamsError =
      validateSendTypingActivityParams(sessionId, typing);

    if (validateSendTypingStatusParamsError) {
      throw validateSendTypingStatusParamsError;
    }

    const store = getStore();

    const { settings, sessions } = store;

    const { showTypingIndicatorWeb } = settings;

    if (!showTypingIndicatorWeb || !sessions[sessionId]) return undefined;

    return sendTypingStatusSignal(sessionId, typing);
  } catch (error) {
    handleError(error, {
      message: 'Failed to send typing status',
    });
  }

  return undefined;
};
